import { useEffect } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';

import { setNavigationPort } from '../services/navigation-port';

/**
 * Wire the router into the navigation port for as long as the authenticated
 * tree is mounted (DV-3013 spec §3.5).
 *
 * Called from `AuthenticatedShell`, which mounts only AFTER `bootstrapAuth`
 * resolves — so the bootstrap's own switches still take `landOn`'s full-page
 * branch, which is what its `'redirecting'` outcome promises the caller. The
 * two login surfaces beneath it (`Loader`, `CallBack`) DO run with the port
 * live; both render nothing but a spinner and unmount when the route changes,
 * so a router landing is simply a faster version of the trip their
 * `'navigating'` outcome already describes.
 *
 * Every landing is `flushSync`, so it renders in the same pass as the
 * `scopeChanged` that `switchEntity` dispatches just before it. Without it the
 * browser renders the two apart: react-redux's update is synchronous and the
 * router's is not, so one render shows the new account on the OLD route. On
 * the account detail page that render is enough for `UnscopedConsole` to clear
 * the account the admin just logged in as, and the landing then bounces to `/`
 * and on to the accounts list (DV-3364 follow-up). jsdom renders the two
 * together, so only the e2e spec `account-detail.spec.ts` catches a regression.
 */
export const useNavigationPort = (): void => {
  const navigate = useNavigate();

  useEffect(() => {
    setNavigationPort((path, queryParams, options) => {
      navigate(
        { pathname: path, search: createSearchParams(queryParams ?? {}).toString() },
        { replace: options?.replace ?? false, flushSync: true },
      );
    });
    return () => setNavigationPort(null);
  }, [navigate]);
};
